import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AssistantActionCreatePortfolioType } from 'types/AssistantTypes';
import { FinecoButton } from '../../FinecoButton';
import { Column, DataSheetGrid, keyColumn, textColumn } from 'react-datasheet-grid';
import 'react-datasheet-grid/dist/style.css';
import useThreadsStore from 'shared/store/threadsStore';
import { useToast } from 'shared/components/ui/use-toast';
import colorsStore from 'shared/store/colorsStore';
import './excelPast.css';
type ExcelPastOneColumnsProps = {
  messagePortfolioCast: AssistantActionCreatePortfolioType;
  onSubmit: (threadId: string, actionId: string, data: any) => Promise<void>;
};

type Row = {
  isin: string | null;
};

const ExcelPastOneColumns: React.FC<ExcelPastOneColumnsProps> = ({ messagePortfolioCast, onSubmit }) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const resetAction = useThreadsStore((state) => state.resetAction);
  const setHideTempStatusUpdate = useThreadsStore((state) => state.setHideTempStatusUpdate);
  const { t } = useTranslation();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [rowsToAdd, setRowsToAdd] = useState(1);

  const [data, setData] = useState<Row[]>(() => {
    if (messagePortfolioCast?.action.input.isins && messagePortfolioCast?.action.input.isins.length > 0) {
      return messagePortfolioCast.action.input.isins.map((isin) => ({
        isin: isin,
      }));
    }
    // If no data, return an empty row
    return [{ isin: '', weight: '' }];
  });
  useEffect(() => {
    setHideTempStatusUpdate(true);
  }, []);

  const baseColumns: Column<Row>[] = [
    {
      ...keyColumn<Row, 'isin'>('isin', textColumn),
      title: 'ISIN',
    },
  ];

  const deleteRow = (rowIndex: number) => {
    setData((prevData) => {
      const newData = [...prevData];
      newData.splice(rowIndex, 1);

      // If we've deleted all rows, add an empty one
      if (newData.length === 0) {
        return [{ isin: '', weight: '' }];
      }

      return newData;
    });
  };

  const dataLengthRef = useRef(-1);
  useEffect(() => {
    dataLengthRef.current = data.length;
  }, [data]);

  const deleteColumn: Column<Row> = {
    title: '',
    width: 30,
    minWidth: 30,
    maxWidth: 30,
    component: ({ rowIndex }) => {
      if (dataLengthRef.current === 1) return null;
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              deleteRow(rowIndex);
            }}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '4px',
              color: colorsConfig.finecoBlack,
              opacity: 1,
              transition: 'opacity 0.2s',
              outline: 'none',
              boxShadow: 'none',
              marginBottom: 10,
              width: 16,
              height: 16,
              WebkitTapHighlightColor: 'transparent',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.opacity = '0.7';
            }}
          >
            <svg
              style={{ width: '16px', height: '16px', flexShrink: 0 }}
              viewBox="0 0 24 24"
              fill="none"
              stroke="#0133cd"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      );
    },
    disabled: true,
  };

  const columns = useMemo(() => {
    return data.length === 1 ? baseColumns : [...baseColumns, deleteColumn];
  }, [data.length]);

  const handleRowChange = (newData: Row[]) => {
    const uppercaseData = newData.map((row) => ({
      ...row,
      isin: row.isin?.toUpperCase() ?? null,
    }));
    setData(uppercaseData);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const body = {
      isins: data.map((row) => row.isin), // Changed from products to isins
    };
    onSubmit(messagePortfolioCast.action.thread_id, messagePortfolioCast.action.id, body)
      .then(() => {
        resetAction(messagePortfolioCast.action.thread_id);
        setSubmitting(false);
        toast({
          title: t('global.success'),
          description: t('assistant.manualPortfolioSuccessOne'),
        });
        setHideTempStatusUpdate(false);
      })
      .catch((error) => {
        setSubmitting(false);
        toast({
          title: t('global.error'),
          description: error?.response?.error || t('assistant.manualPortfolioError'), // Updated to show error message
          variant: 'destructive',
        });
      });
  };

  const isSubmitDisabled = data.length === 0 || data.every((row) => !row.isin);

  useEffect(() => {
    const handleCellClick = (e) => {
      const cell = e.target.closest('.dsg-cell');
      if (cell) {
        const input = cell.querySelector('input');
        if (input) {
          setTimeout(() => {
            input.focus();
            input.select();
          }, 0);
        }
      }
    };

    const grid = document.querySelector('.custom-datasheet-wrapper');
    if (grid) {
      grid.addEventListener('click', handleCellClick);
    }

    return () => {
      if (grid) {
        grid.removeEventListener('click', handleCellClick);
      }
    };
  }, []);

  return (
    <div className="excelContainer">
      <div className="excelContainerInner">
        <h2 style={{ color: colorsConfig.finecoBlack, marginBottom: '20px' }}>
          {t('assistant.createManualPortfolioStrumenti')}
        </h2>

        <div className="excelEditor">
          <div
            className="custom-datasheet-wrapper custom-datasheet-wrapper-single"
            style={{ maxWidth: 'calc(100% - 20px)' }}
          >
            <DataSheetGrid
              key={data.length === 1 ? 'single' : 'multiple'}
              value={data}
              onChange={handleRowChange}
              columns={columns}
              addRowsComponent={({ addRows }) => (
                <div className="dsg-add-row">
                  <button className="dsg-add-row-btn" onClick={() => addRows(rowsToAdd)}>
                    + Add
                  </button>
                  <input
                    className="dsg-add-row-input"
                    type="number"
                    min={1}
                    value={rowsToAdd}
                    onChange={(e) => setRowsToAdd(Number(e.target.value))}
                  />
                  <span style={{ marginLeft: 6 }}>Rows</span>
                </div>
              )}
            />
          </div>
        </div>
        <div
          style={{
            marginTop: '20px',
            display: 'flex',
            justifyContent: 'end',
          }}
        >
          <FinecoButton
            click={handleSubmit}
            copy={submitting ? t('actions.submitting') : t('actions.submit')}
            disabled={isSubmitDisabled}
          />
        </div>
      </div>
    </div>
  );
};

export default ExcelPastOneColumns;
